import { useEffect, useState } from "react";
import Faqs from "../../components/Faqs";
import Footer from "../../components/layouts/Footer";
import HowItWorks from "../../components/HowItWorks";
import LetsGetInTouch from "../../components/LetsGetInTouch";
import { usePersistStore } from "../../store";
import { DASHBOARD_TYPE } from "../../utils/enums";
import { Link, useLocation } from "react-router-dom";

type QuickLink = {
  icon: string;
  title: string;
  description: string;
  href: string;
  external?: boolean;
};

const QUICK_LINKS: QuickLink[] = [
  {
    icon: "bi-compass",
    title: "How it works",
    description: "Walk through the closed-bid flow end to end.",
    href: "#HowItWorks",
  },
  {
    icon: "bi-question-circle",
    title: "Browse FAQs",
    description: "Quick answers to the questions we hear most.",
    href: "#Faqs",
  },
  {
    icon: "bi-chat-dots",
    title: "Contact support",
    description: "Send us a message and we'll get back to you.",
    href: "#ContactUs",
  },
  {
    icon: "bi-shield-check",
    title: "Terms & policies",
    description: "Read the platform agreement that protects everyone.",
    href: "/terms-and-conditions",
    external: true,
  },
];

function HelpAndSupport() {
  const location = useLocation();
  const { dashboardType } = usePersistStore((s: any) => s);
  const [activeTab, setActiveTab] = useState(dashboardType);
  const [keyword, setKeyword] = useState<string>("");

  useEffect(() => {
    setActiveTab(dashboardType);
  }, [dashboardType]);

  useEffect(() => {
    setTimeout(() => {
      let s = location.search?.split("?to=")[1];
      if (s === "how-it-works") {
        document.getElementById("HowItWorks")?.scrollIntoView({
          behavior: "smooth",
        });
      } else if (s === "faqs") {
        document.getElementById("Faqs")?.scrollIntoView({
          behavior: "smooth",
        });
      } else if (s === "contact-us") {
        document.getElementById("ContactUs")?.scrollIntoView({
          behavior: "smooth",
        });
      }
    }, 300);
  }, [location.search]);

  const handleQuickLink = (href: string) => {
    if (href.startsWith("#")) {
      const id = href.slice(1);
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <section className="helpHero">
        <div className="container">
          <nav className="helpHero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span>Help &amp; Support</span>
          </nav>

          <div className="helpHero__inner">
            <span className="helpHero__badge">
              <i className="bi bi-life-preserver" aria-hidden="true" />
              Help center
            </span>
            <h1>How can we help you?</h1>
            <p className="helpHero__lead">
              Browse the answers below, walk through how Boll-e&trade; works, or send
              us a note &mdash; we usually reply within a business day.
            </p>
          </div>
        </div>
      </section>

      <div className="container helpQuick">
        <div className="row g-3 g-md-4">
          {QUICK_LINKS.map((link) => {
            const body = (
              <>
                <span className="helpQuick__icon">
                  <i className={`bi ${link.icon}`} aria-hidden="true" />
                </span>
                <span className="helpQuick__body">
                  <span className="helpQuick__title">{link.title}</span>
                  <span className="helpQuick__desc">{link.description}</span>
                </span>
                <i
                  className="bi bi-arrow-up-right helpQuick__arrow"
                  aria-hidden="true"
                />
              </>
            );

            return (
              <div
                key={link.title}
                className="col-12 col-sm-6 col-lg-3"
              >
                {link.external ? (
                  <Link to={link.href} className="helpQuick__card">
                    {body}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleQuickLink(link.href)}
                    className="helpQuick__card"
                  >
                    {body}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div id="HowItWorks" />
      <HowItWorks showSwitch={true} />

      <section id="Faqs" className="helpFaqs">
        <div className="container">
          <div className="helpFaqs__header">
            <span className="helpFaqs__kicker">FAQs</span>
            <h2>Frequently asked questions</h2>
            <p>
              Find answers to common questions about buying and selling on
              Boll-e&trade; &mdash; payments, listings, shipping, and more. Don't see
              what you're after? Drop us a message below.
            </p>
          </div>

          <div className="helpFaqs__controls">
            <div
              className="dashModeSwitch"
              role="tablist"
              aria-label="FAQ audience"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === DASHBOARD_TYPE.BUYER}
                className={`dashModeSwitch__opt ${
                  activeTab === DASHBOARD_TYPE.BUYER ? "is-active" : ""
                }`}
                onClick={() => setActiveTab(DASHBOARD_TYPE.BUYER)}
              >
                Buyer
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === DASHBOARD_TYPE.SELLER}
                className={`dashModeSwitch__opt ${
                  activeTab === DASHBOARD_TYPE.SELLER ? "is-active" : ""
                }`}
                onClick={() => setActiveTab(DASHBOARD_TYPE.SELLER)}
              >
                Seller
              </button>
            </div>

            <div className="helpFaqs__search">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                type="search"
                placeholder="Filter questions…"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setKeyword("")}
                >
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="helpFaqs__content">
            <Faqs type={activeTab} keyword={keyword} />
          </div>
        </div>
      </section>

      <div id="ContactUs" />
      <LetsGetInTouch />
      <Footer />
    </>
  );
}

export default HelpAndSupport;
